import React, { Component } from "react";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import ScopeFilters from "./ScopeFilters";
import ExportButtons from "./ExportButtons";
import { exportExcel, exportPdf } from "./misExport";
import { TableShimmer } from "../preloader/PageCardShimmer";
import { headerBranches } from "./misScope";

const GET_SPA = gql`
  query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getStudentPerformanceAnalysis(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      kpis { totalStudents avgScore improving declining }
      classes
      classSubjectAvg { subject values }
      testLabels
      testTrend { name data }
      top10 { rank name id studentUserId class avg subs }
      bottom10 { name id studentUserId class avg att }
      allStudents { rank studentUserId name admissionNo rollNo className sectionName branchName mobile tests avg best worst }
    }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
   Student Performance Analysis — Management MIS Reports tab.
   API-READY: all data comes from props and defaults to empty. Wire a real
   query (e.g. getStudentPerformanceAnalysis) and pass the results as props.

   Expected props (all optional):
     kpis: { totalStudents, avgScore, improving, declining }
     classes: ["Class 11 MPC", ...]            // x-axis / heatmap columns
     classAvgBySubject: { Physics: [..per class], ... }
     testTrend: [{ name, data: [..] }], testLabels: ["T1", ...]
     chapters: [..], heatmap: [[..per class], ..]
     top10: [{ rank, name, id, class, avg, subs }]
     bottom10: [{ name, id, class, avg, att }]
     faculty: [{ name, value }]
     historical: { categories: [..], series: [{ name, data }] }
   ────────────────────────────────────────────────────────────────────────── */

const PURPLE = "#7c3aed";

const KPI_META = [
  { key: "totalStudents", label: "Total Students", icon: "fal fa-users", color: "#6366f1" },
  { key: "avgScore", label: "Average Score", icon: "fal fa-bullseye-arrow", color: "#16a34a" },
];

const AVATAR_COLORS = ["#f59e0b", "#6366f1", "#8b5cf6", "#10b981", "#ec4899", "#3b82f6", "#ef4444", "#14b8a6", "#f97316"];
const initials = (name) => (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const colorFor = (s) => AVATAR_COLORS[((s || "?").charCodeAt(0) + (s || "").length) % AVATAR_COLORS.length];

const S = {
  page: { background: "#f1f5f9", padding: "20px 24px 60px" },
  hRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  crumb: { fontSize: 12, color: "#94a3b8", marginBottom: 4 },
  h1: { fontSize: 24, fontWeight: 800, color: "#0f172a", margin: 0 },
  sub: { fontSize: 13, color: "#64748b", marginTop: 4 },
  btnGhost: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "#334155", cursor: "pointer" },
  btnPrimary: { background: PURPLE, border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer" },
  card: { background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  cardPad: { padding: "18px 20px" },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 12 },
  filters: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 16px", marginBottom: 16 },
  select: { borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "8px 10px", fontSize: 13, color: "#1f2937", background: "#f9fafb", minWidth: 130 },
  banner: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#92400e", marginBottom: 16 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 16 },
  kpiCard: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px" },
  kpiLabel: { fontSize: 13, color: "#64748b", fontWeight: 600 },
  kpiValue: { fontSize: 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.1, margin: "4px 0" },
  kpiIcon: (c) => ({ width: 46, height: 46, borderRadius: 12, background: c + "1a", color: c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }),
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: 16, marginBottom: 16 },
  th: { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #e2e8f0" },
  td: { fontSize: 13, color: "#334155", padding: "9px 10px", borderBottom: "1px solid #f1f5f9" },
  avatar: (c) => ({ width: 30, height: 30, borderRadius: 8, background: c, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, marginRight: 8 }),
  riskPill: { background: "#fee2e2", color: "#dc2626", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 },
  empty: { padding: "28px 10px", textAlign: "center", color: "#94a3b8", fontSize: 13 },
  // Dummy/sample sections are rendered muted + grayscale to signal they aren't live.
  dummy: { filter: "grayscale(1)", opacity: 0.55 },
  sampleTag: { fontSize: 10, fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", marginLeft: 8, verticalAlign: "middle", textTransform: "uppercase", letterSpacing: 0.4 },
};

const GRAY = "#94a3b8";

const toCsv = (arr) => (arr && arr.length ? arr.join(",") : null);
const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));
const effectiveBranchCsv = (scope, sel) => {
  // Branch is chosen ONCE in the header (NavbarOne) and applies to every tab; State/City
  // only narrow within that selection. See utils/branchScope + misScope.headerBranches.
  const scoped = headerBranches(scope).filter((b) => inSel(sel.state, b.state_id) && inSel(sel.city, b.city_id));
  return toCsv(scoped.map((b) => b.id));
};
const EMPTY_SPA = { kpis: {}, classes: [], classSubjectAvg: [], testLabels: [], testTrend: [], top10: [], bottom10: [], allStudents: [] };

class StudentPerformanceAnalysisTab extends Component {
  state = { sel: { state: [], city: [], branch: [], class: [], stream: [], section: [], exam: [] }, spa: null, loading: true };

  // Exam filter is client-side (just hides x-axis columns) — no refetch needed.
  // Scope change: re-default the Exam picker to "all" for the new scope's exams.
  setSel = (nextSel) => { this._examDefaulted = false; this.setState({ sel: nextSel }, this.load); };

  // Navigate to the student's performance profile.
  openStudentProfile = (s) => {
    const student = {
      student_user_id: s.studentUserId ? parseInt(s.studentUserId) : null,
      student_name: s.name,
      class_name: s.class,
      contact_no: s.id,
      student_mobile: s.id,
    };
    this.props.history.push("/student-profile-1/overview", { student });
  };

  componentDidMount() { this.defaultSelAll(); this.load(); }

  // The header's branch selector hands down a NEW scope object; re-default and refetch when it
  // changes. Previously this only called defaultSelAll(), which returns early once it has run,
  // so after the first load the tab ignored the header entirely — the branch was still sent on
  // the request, but the request was never made again.
  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope) {
      this._selDefaulted = false;
      this._examDefaulted = false;
    }
    this.defaultSelAll();
  }

  // Default every scope dropdown to "all selected" once the scope data is available.
  defaultSelAll = () => {
    if (this._selDefaulted) return;
    const sc = this.props.scope || {};
    const hasAny = (sc.states || []).length || (sc.cities || []).length || (sc.branches || []).length || (sc.classes || []).length || (sc.sections || []).length;
    if (!hasAny) return;
    this._selDefaulted = true;
    this.setState({
      sel: {
        state: (sc.states || []).map((s) => s.state_id),
        city: (sc.cities || []).map((c) => c.id),
        branch: (sc.branches || []).map((b) => b.id),
        class: [...new Set((sc.classes || []).map((c) => c.id))],
        stream: [...new Set((sc.streams || []).map((s) => s.id))],
        section: (sc.sections || []).map((s) => s.id),
      },
    }, this.load);
  };

  // Fetch the analysis for the selected branch/class (re-runs on filter change).
  load = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    const { sel } = this.state;
    this.setState({ loading: true });
    try {
      const res = await this.props.client.query({
        query: GET_SPA,
        variables: { institute_id: instId, branch_id: effectiveBranchCsv(this.props.scope || {}, sel), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
        fetchPolicy: "network-only",
      });
      const spa = (res.data && res.data.getStudentPerformanceAnalysis) || EMPTY_SPA;
      const patch = { spa, loading: false };
      // By default select every exam in the trend once data is available.
      if (!this._examDefaulted && (spa.testLabels || []).length) {
        this._examDefaulted = true;
        patch.sel = { ...this.state.sel, exam: [...new Set(spa.testLabels)] };
      }
      this.setState(patch);
    } catch (e) { console.error("student performance", e); this.setState({ spa: EMPTY_SPA, loading: false }); }
  };

  exportTables = () => {
    const d = this.state.spa || EMPTY_SPA;
    const classes = d.classes || [];
    const tables = [
      {
        heading: `Top Performers (${(d.top10 || []).length})`,
        columns: ["Rank", "Student", "ID", "Class", "Avg", "Tests"],
        rows: (d.top10 || []).map((s) => [s.rank, s.name, s.id, s.class, s.avg, s.subs]),
      },
      {
        heading: `Bottom Performers (${(d.bottom10 || []).length})`,
        columns: ["Student", "ID", "Class", "Avg", "Tests"],
        rows: (d.bottom10 || []).map((s) => [s.name, s.id, s.class, s.avg, s.att]),
      },
    ];
    // Class × subject accuracy — a column per class, a row per subject.
    if ((d.classSubjectAvg || []).length && classes.length) {
      tables.push({
        heading: "Class × Subject Accuracy (%)",
        columns: ["Subject", ...classes],
        rows: (d.classSubjectAvg || []).map((x) => [x.subject, ...(x.values || []).map((v) => (v == null ? "" : `${v}%`))]),
      });
    }
    return tables;
  };

  handleExcel = () => exportExcel(this.exportTables().map((x) => ({ name: x.heading.replace(/\s*\(\d+\)$/, ""), aoa: [x.columns, ...x.rows] })), "student_performance_analysis.xlsx");
  handlePdf = () => exportPdf("Student Performance Analysis", this.exportTables(), "student_performance_analysis.pdf");

  // Every student in scope with their own numbers, searchable. A student who has never sat a
  // test shows "—" for rank and average rather than 0% — not sitting an exam and scoring
  // nothing are different facts, and 0% would sort them alongside genuine low performers.
  renderAllStudents() {
    const all = (this.state.spa && this.state.spa.allStudents) || [];
    const q = (this.state.studentSearch || "").trim().toLowerCase();
    const filtered = q
      ? all.filter((s) => [s.name, s.admissionNo, s.rollNo, s.sectionName, s.className, s.branchName]
        .some((v) => String(v || "").toLowerCase().includes(q)))
      : all;

    // Sorting. Default is the server's order (rank), which already puts unscored students last.
    // Nulls always sink to the bottom whichever direction is chosen — a student with no test
    // shouldn't top an "ascending average" sort ahead of someone who genuinely scored 3%.
    const { key: sortKey, dir: sortDir } = this.state.studentSort || {};
    const rows = !sortKey ? filtered : [...filtered].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "desc" ? -cmp : cmp;
    });
    // Click cycles asc -> desc -> off, so the original ranking is always one click away.
    const toggleSort = (key) => this.setState((s) => {
      const cur = s.studentSort || {};
      if (cur.key !== key) return { studentSort: { key, dir: "asc" } };
      if (cur.dir === "asc") return { studentSort: { key, dir: "desc" } };
      return { studentSort: {} };
    });
    const SortTh = ({ label, sKey, center }) => (
      <th
        style={{ ...S.th, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", textAlign: center ? "center" : "left" }}
        onClick={() => toggleSort(sKey)}
        title={`Sort by ${label}`}
      >
        {label}
        <span style={{ marginLeft: 5, color: sortKey === sKey ? "#7c3aed" : "#cbd5e1", fontSize: 10 }}>
          {sortKey === sKey ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
      </th>
    );
    const withTests = all.filter((s) => s.tests > 0).length;
    const th = { ...S.th, whiteSpace: "nowrap" };
    const td = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
    const pct = (v) => (v == null ? <span style={{ color: "#94a3b8" }}>—</span>
      : <span style={{ fontWeight: 700, color: v >= 60 ? "#16a34a" : v >= 40 ? "#ea580c" : "#dc2626" }}>{v}%</span>);

    return (
      <div style={{ ...S.card, ...S.cardPad, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <div style={S.cardTitle}>
            All Students ({rows.length}{q ? ` of ${all.length}` : ""})
            <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 12, marginLeft: 8 }}>
              {withTests} with test data · {all.length - withTests} yet to sit one
            </span>
          </div>
          <input
            type="text" value={this.state.studentSearch || ""}
            onChange={(e) => this.setState({ studentSearch: e.target.value })}
            placeholder="Search name, admission, roll, section…"
            style={{ minWidth: 260, fontSize: 13, padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}
          />
        </div>
        {this.state.loading ? <TableShimmer rows={10} cols={8} /> : (
          <div style={{ maxHeight: 520, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th style={{ ...S.th, whiteSpace: "nowrap" }}>#</th>
                  <SortTh label="Rank" sKey="rank" />
                  <SortTh label="Student" sKey="name" />
                  <SortTh label="Admission" sKey="admissionNo" />
                  <SortTh label="Roll" sKey="rollNo" />
                  <SortTh label="Class" sKey="className" />
                  <SortTh label="Section" sKey="sectionName" />
                  <SortTh label="Branch" sKey="branchName" />
                  <SortTh label="Tests" sKey="tests" center />
                  <SortTh label="Avg" sKey="avg" center />
                  <SortTh label="Best" sKey="best" center />
                  <SortTh label="Worst" sKey="worst" center />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={12} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: 24 }}>No students in this selection.</td></tr>
                ) : rows.map((s, i) => (
                  <tr key={`${s.admissionNo || s.mobile}-${i}`}>
                    {/* Serial number of the row as displayed, so every student is numbered and
                        the count stays continuous through sorting and searching. The
                        performance rank has its own column — it is only meaningful for
                        students who have actually sat something. */}
                    <td style={{ ...td, color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ ...td, color: "#94a3b8" }}>{s.rank == null ? "—" : s.rank}</td>
                    <td style={{ ...td, fontWeight: 600, color: "#1f2937" }}>{s.name}</td>
                    <td style={td}>{s.admissionNo || "—"}</td>
                    <td style={td}>{s.rollNo || "—"}</td>
                    <td style={td}>{s.className || "—"}</td>
                    <td style={td}>{s.sectionName || "—"}</td>
                    <td style={td}>{s.branchName || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.tests}</td>
                    <td style={{ ...td, textAlign: "center" }}>{pct(s.avg)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{pct(s.best)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{pct(s.worst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  render() {
    const data = this.state.spa || EMPTY_SPA;
    // classSubjectAvg / testTrend / testLabels are still fetched — the Excel and PDF exports
    // build sheets from them — but nothing on screen charts them any more.
    const { kpis = {}, top10 = [], bottom10 = [] } = data;

    // Total students from the analysis KPIs (students with test activity in scope).
    const realTotal = this.state.loading ? null : (kpis.totalStudents != null ? kpis.totalStudents : null);

    return (
      <div style={S.page}>
        {/* Header */}
        <div style={S.hRow}>
          <div>
            <div style={S.crumb}>Student Performance Analysis</div>
            <h1 style={S.h1}>Student Performance Analysis {this.state.loading && <i className="fal fa-spinner fa-spin" style={{ fontSize: 16, color: "#7c3aed", marginLeft: 8 }} />}</h1>
            <div style={S.sub}>Individual &amp; class-level analytics across classes, branches and subjects</div>
          </div>
          <ExportButtons onExcel={this.handleExcel} onPdf={this.handlePdf} disabled={this.state.loading} />
        </div>

        {/* Scope filters — cascading State -> City -> Branch -> Class -> Section */}
        <ScopeFilters scope={this.props.scope} sel={this.state.sel} onChange={this.setSel} style={{ ...S.card, ...S.filters }} />

        {/* KPI cards — Total Students is live; others come from the API (empty for now). */}
        <div style={S.kpiGrid}>
          {KPI_META.map((k) => {
            const isLive = k.key === "totalStudents";
            let value = isLive
              ? (realTotal != null ? realTotal : "…")
              : (kpis[k.key] != null ? kpis[k.key] : "—");
            // Average Score is a percentage.
            if (k.key === "avgScore" && kpis.avgScore != null) value = `${kpis.avgScore}%`;
            return (
              <div key={k.label} style={{ ...S.card, ...S.kpiCard }}>
                <div>
                  <div style={S.kpiLabel}>{k.label}</div>
                  <div style={S.kpiValue}>{value}</div>
                </div>
                <div style={S.kpiIcon(k.color)}><i className={k.icon} /></div>
              </div>
            );
          })}
        </div>

        {/* The Class-wise Average and Exam-wise Trend charts were removed: this tab is now a
            roster — every student in scope with their own performance — rather than two
            aggregate views that said less than the table below them. */}
        {this.renderAllStudents()}

        {/* Top + Bottom performers */}
        {this.state.loading ? (
          <div style={{ ...S.card, ...S.cardPad, marginBottom: 16 }}><TableShimmer rows={8} cols={5} /></div>
        ) : (
        <div style={S.grid2}>
          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>🏆 Top 10 Performers — with subject breakdown</div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <table style={{ width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}><tr><th style={S.th}>#</th><th style={S.th}>Student</th><th style={S.th}>Class</th><th style={S.th}>Avg</th><th style={S.th}>Subjects</th></tr></thead>
              <tbody>
                {top10.length === 0 ? (
                  <tr><td colSpan={5} style={S.empty}>No data</td></tr>
                ) : top10.map((s) => (
                  <tr key={s.id} onClick={() => this.openStudentProfile(s)} style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#faf5ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")} title="View student profile">
                    <td style={S.td}>{s.rank}</td>
                    <td style={S.td}>
                      <span style={S.avatar(colorFor(s.name))}>{initials(s.name)}</span>
                      <span style={{ fontWeight: 600, color: PURPLE, textDecoration: "underline" }}>{s.name}</span><br />
                      <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 38, display: "inline-block" }}>{s.id}</span>
                    </td>
                    <td style={S.td}>{s.class}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: PURPLE }}>{s.avg}</td>
                    <td style={{ ...S.td, fontSize: 11, color: "#64748b" }}>{s.subs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>⚠️ Needs Intervention — Lowest 10</div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <table style={{ width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}><tr><th style={S.th}>Student</th><th style={S.th}>Class</th><th style={S.th}>Avg</th><th style={S.th}>Att</th><th style={S.th}>Risk</th></tr></thead>
              <tbody>
                {bottom10.length === 0 ? (
                  <tr><td colSpan={5} style={S.empty}>No data</td></tr>
                ) : bottom10.map((s) => (
                  <tr key={s.id} onClick={() => this.openStudentProfile(s)} style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2" )}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")} title="View student profile">
                    <td style={S.td}>
                      <span style={S.avatar(colorFor(s.name))}>{initials(s.name)}</span>
                      <span style={{ fontWeight: 600, color: "#dc2626", textDecoration: "underline" }}>{s.name}</span><br />
                      <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 38, display: "inline-block" }}>{s.id}</span>
                    </td>
                    <td style={S.td}>{s.class}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: "#dc2626" }}>{s.avg}</td>
                    <td style={S.td}>{s.att}</td>
                    <td style={S.td}><span style={S.riskPill}>High Risk</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
        )}

      </div>
    );
  }
}

export default withRouter(withApollo(StudentPerformanceAnalysisTab));
